import React, { useCallback, useContext, useEffect, useState } from 'react';
import { SearchBarContext } from '../../client/hooks';

import { SearchBar } from '../input/SearchBar';
import { FilterIcon } from '../svg/FilterIcon';

type Props = {
    className?: string;
    title: string;
    filters?: JSX.Element[];
    filtersAlwaysActive?: boolean;
    isStandaloneHeader: boolean;
    customAsyncSearch?: (s: string, filtersActive: boolean) => Promise<any>;
    hasSearchButton: boolean;
    searchPlaceholder?: string;
    defaultValue?: string;
    initialSearch?: boolean;
};

export const PageListHeader: React.FC<Props> = ({
    className,
    isStandaloneHeader,
    title,
    filters,
    filtersAlwaysActive,
    customAsyncSearch,
    hasSearchButton,
    searchPlaceholder,
    defaultValue,
    initialSearch,
}) => {
    const [filtersActive, setFiltersActive] = useState<boolean>(
        (filtersAlwaysActive || initialSearch) ?? filtersAlwaysActive,
    );
    const [searchValue, setSearchValue] = useState<string>(defaultValue);
    // use generic one
    const { dispatch } = useContext(SearchBarContext);
    const onSearchFieldChange = (event) => {
        setSearchValue(event.target.value);
    };
    const handleEnterEvent = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleSearch(searchValue);
        }
    };
    const handleSearch = useCallback(
        async (v: string) => {
            // if search is async, call it, dispatch a loading event
            // then, on result, dispatch results
            if (!!customAsyncSearch) {
                const results = await customAsyncSearch(v, filtersActive);

                dispatch({
                    type: 'asyncSearchCompleted',
                    results: results.results,
                    totalResults: results.count,
                    lastSearchTerm: v,
                    filtersActive,
                });
                //
            } else {
                // pass in filters too when they change
                dispatch({ type: 'searchData', value: v, useFilter: filtersActive });
            }
        },
        [customAsyncSearch, dispatch, filtersActive],
    );

    return (
        <div className={`page-list-header${className ? ` ${className}` : ''}`}>
            <div className="container">
                <div className="title-container">
                    {isStandaloneHeader ? (
                        <h1 className="page-list-title">{title}</h1>
                    ) : (
                        <h2 className="page-list-title">{title}</h2>
                    )}
                    <div className="search-container">
                        {!filtersAlwaysActive && filters && (
                            <>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setFiltersActive(!filtersActive);
                                    }}
                                    className={`button filter-button
                                    ${filtersActive ? ' active' : ''}
                                    ${isStandaloneHeader ? ' dark-theme' : ''}`}
                                >
                                    <FilterIcon />
                                </button>
                            </>
                        )}
                        <SearchBar
                            handleEnterEvent={handleEnterEvent}
                            onChange={onSearchFieldChange}
                            hasSearchButton={hasSearchButton}
                            placeholder={searchPlaceholder}
                            defaultValue={defaultValue}
                            onSearch={handleSearch}
                            className={`${isStandaloneHeader ? 'dark-theme' : ''}`}
                        />
                    </div>
                </div>
                {filtersActive && <div className="filters">{filters}</div>}
                {!hasSearchButton && (
                    <button
                        className="button medium-button blue-button outer-search-button"
                        onClick={() => handleSearch(searchValue)}
                    >
                        Search
                    </button>
                )}
            </div>
        </div>
    );
};
